import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    // Only verify token if we're NOT on the login page
    if (token && window.location.pathname !== '/login' && window.location.pathname !== '/') {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    const delaysMs = [0, 400, 1000, 2000];
    let lastError;

    for (let i = 0; i < delaysMs.length; i++) {
      if (delaysMs[i] > 0) {
        await new Promise((r) => setTimeout(r, delaysMs[i]));
      }
      try {
        const response = await api.get('/auth/verify');
        const payload = response.data;
        const user = payload && typeof payload === 'object' ? payload.user : null;

        if (!user || typeof user !== 'object') {
          localStorage.removeItem('token');
          setUser(null);
          if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            window.location.href = '/';
          }
          setLoading(false);
          return;
        }

        if (user.status !== 'active') {
          localStorage.removeItem('token');
          setUser(null);
          if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
            window.location.href = '/';
          }
          setLoading(false);
          return;
        }

        setUser(user);
        setLoading(false);
        return;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        // Mongo still connecting (requireDBConnection) — retry without discarding JWT
        if (status === 503 && i < delaysMs.length - 1) {
          continue;
        }
        break;
      }
    }

    const status = lastError?.response?.status;
    if (status === 503) {
      // DB unavailable after retries: keep token so a refresh works when DB is ready
      setUser(null);
      setLoading(false);
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/';
      }
      return;
    }

    localStorage.removeItem('token');
    setUser(null);
    if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
      window.location.href = '/';
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const data = response.data;
      if (typeof data === 'string' && /^\s*</.test(data)) {
        return {
          success: false,
          error:
            'Server returned a web page instead of JSON. The API is not running on this URL (often a static file server is bound to PORT).',
        };
      }
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return { success: false, error: 'Invalid response from server' };
      }
      const { token, user } = data;
      if (!token || !user) {
        return {
          success: false,
          error: data.error || data.message || 'Login failed',
        };
      }

      localStorage.setItem('token', token);
      setUser(user);

      return { success: true, user };
    } catch (error) {
      // Return the error code and message for better handling
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.error || errorData?.message || 'Login failed',
        message: errorData?.message || errorData?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/verify');
      const userData = response.data.user;
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
